import ModalBody from '@/components/Modal/common/ModalBody';
import ModalLoadingBackdrop from '@/components/Modal/common/ModalLoadingBackdrop';
import ModalTitleCard from '@/components/Modal/common/ModalTitleCard';
import { NEW_PASSWORD_RULES } from '@/utils/validationRules';
import { VisibilityOffOutlined, VisibilityOutlined } from '@mui/icons-material';
import { Button, FormLabel, IconButton, InputAdornment, Modal, TextField, styled } from '@mui/material';
import { HttpStatusCode } from 'axios';
import PropTypes from 'prop-types';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import PasswordStrengthStepper from './PasswordStrengthStepper';
import { changePassword } from './data/changePasswordService';

const ActionButton = styled(Button)({
  height: '48px',
  fontSize: 16,
});

const ChangePasswordModal = ({ email = '', open, handleClose }) => {
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfrimNewPassword, setShowConfrimNewPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleClickShowCurrentPassword = () => setShowCurrentPassword(show => !show);
  const handleClickShowNewPassword = () => setShowNewPassword(show => !show);
  const handleClickShowConfirmNewPassword = () => setShowConfrimNewPassword(show => !show);
  const handleMouseDownPassword = event => {
    event.preventDefault();
  };

  const { control, handleSubmit, reset, setError, clearErrors, watch } = useForm({
    defaultValues: {
      email,
      currentPassword: '',
      newPassword: '',
      confirmNewPassword: '',
    },
  });

  const onModalClose = (_, reason) => {
    if (reason) return;
    handleClose();
    reset();
  };

  const onSubmit = async data => {
    setLoading(true);
    clearErrors('currentPassword');
    try {
      await changePassword(data.currentPassword, data.newPassword);
      toast.success('Password updated');
      setLoading(false);
      onModalClose();
    } catch (error) {
      setLoading(false);
      if (
        error.response?.status === HttpStatusCode.BadRequest &&
        error.response?.data?.errors?.some(err => err.message.includes('E_INVALID_AUTH_PASSWORD'))
      ) {
        setError('currentPassword', { message: 'Incorrect password', type: 'manual' });
        return;
      } else {
        toast.error('Something went wrong');
        return;
      }
    }
  };

  const password = watch('newPassword');

  return (
    <>
      <Modal open={open} onClose={onModalClose} aria-labelledby='modal-title'>
        <ModalBody
          sx={{
            minWidth: 'unset',
            maxWidth: 540,
            width: 540,
          }}
        >
          <ModalTitleCard
            handleClose={onModalClose}
            sx={{
              alignItems: 'center',
              padding: '28px 32px',
              gap: '26px',
            }}
            titleIcon={
              <div className='bg-[#C0C9C6] w-[73px] h-[73px] rounded-full flex items-center justify-center'>
                <svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
                  <path
                    d='M18 4H15.75V2.75C15.75 2.34 15.41 2 15 2C14.59 2 14.25 2.34 14.25 2.75V21.25C14.25 21.66 14.59 22 15 22C15.41 22 15.75 21.66 15.75 21.25V20H18C20.21 20 22 18.21 22 16V8C22 5.79 20.21 4 18 4Z'
                    fill='#292D32'
                  />
                  <path
                    d='M6.5 4C4.29 4 2.5 5.79 2.5 8V16C2.5 18.21 4.29 20 6.5 20H11.5C12.05 20 12.5 19.55 12.5 19V5C12.5 4.45 12.05 4 11.5 4H6.5ZM6.67 12.38C6.62 12.5 6.55 12.61 6.46 12.71C6.36 12.8 6.25 12.87 6.13 12.92C6.01 12.97 5.88 13 5.75 13C5.62 13 5.49 12.97 5.37 12.92C5.25 12.87 5.14 12.8 5.04 12.71C4.95 12.61 4.88 12.5 4.82 12.38C4.77 12.26 4.75 12.13 4.75 12C4.75 11.74 4.86 11.48 5.04 11.29C5.09 11.25 5.14 11.21 5.19 11.17C5.25 11.13 5.31 11.1 5.37 11.08C5.43 11.05 5.49 11.03 5.55 11.02C5.89 10.95 6.23 11.06 6.46 11.29C6.64 11.48 6.75 11.74 6.75 12C6.75 12.13 6.72 12.26 6.67 12.38ZM10.17 12.38C10.12 12.5 10.05 12.61 9.96 12.71C9.86 12.8 9.75 12.87 9.63 12.92C9.51 12.97 9.38 13 9.25 13C9.12 13 8.99 12.97 8.87 12.92C8.75 12.87 8.64 12.8 8.54 12.71C8.35 12.52 8.25 12.27 8.25 12C8.25 11.87 8.28 11.74 8.33 11.62C8.38 11.49 8.45 11.39 8.54 11.29C8.91 10.92 9.58 10.92 9.96 11.29C10.14 11.48 10.25 11.74 10.25 12C10.25 12.13 10.22 12.26 10.17 12.38Z'
                    fill='#292D32'
                  />
                </svg>
              </div>
            }
            title='Change Password'
            titleClassname=''
          />
          <form className='mt-[22px]' onSubmit={handleSubmit(onSubmit)}>
            {/* Hidden email input for password browser save completeness */}
            <Controller
              name='email'
              control={control}
              render={({ field }) => <input {...field} autoComplete='email' className='hidden' />}
            />
            <FormLabel htmlFor='current-password-input'>Current Password</FormLabel>
            <Controller
              name='currentPassword'
              control={control}
              rules={{
                required: {
                  value: true,
                  message: 'Current Password is required.',
                },
              }}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  id='current-password-input'
                  type={showCurrentPassword ? 'text' : 'password'}
                  autoComplete='current-password'
                  fullWidth
                  error={!!error}
                  helperText={error?.message}
                  sx={{
                    marginBottom: '16px',
                  }}
                  slotProps={{
                    input: {
                      endAdornment: (
                        <InputAdornment position='end'>
                          <IconButton
                            aria-label='toggle current password visibility'
                            onClick={handleClickShowCurrentPassword}
                            onMouseDown={handleMouseDownPassword}
                            edge='end'
                          >
                            {showCurrentPassword ? <VisibilityOffOutlined /> : <VisibilityOutlined />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    },

                    htmlInput: { placeholder: 'Enter Current Password' },
                  }}
                />
              )}
            />
            <FormLabel htmlFor='new-password-input'>New Password</FormLabel>
            <Controller
              name='newPassword'
              control={control}
              rules={NEW_PASSWORD_RULES}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  id='new-password-input'
                  type={showNewPassword ? 'text' : 'password'}
                  autoComplete='new-password'
                  fullWidth
                  error={!!error}
                  helperText={error?.message}
                  sx={{
                    marginBottom: '16px',
                  }}
                  slotProps={{
                    input: {
                      endAdornment: (
                        <InputAdornment position='end'>
                          <IconButton
                            aria-label='toggle new password visibility'
                            onClick={handleClickShowNewPassword}
                            onMouseDown={handleMouseDownPassword}
                            edge='end'
                          >
                            {showNewPassword ? <VisibilityOffOutlined /> : <VisibilityOutlined />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    },

                    htmlInput: { placeholder: 'Enter New Password' },
                  }}
                />
              )}
            />
            <div className='mb-[20px]'>
              <PasswordStrengthStepper password={password} />
            </div>
            <FormLabel htmlFor='confirm-new-password-input'>Confirm New Password</FormLabel>
            <Controller
              name='confirmNewPassword'
              control={control}
              rules={{
                required: {
                  value: true,
                  message: 'Please confirm new password.',
                },
                validate: (value, formValues) => value === formValues.newPassword || "Passwords don't match",
              }}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  id='confirm-new-password-input'
                  fullWidth
                  error={!!error}
                  helperText={error?.message}
                  type={showConfrimNewPassword ? 'text' : 'password'}
                  slotProps={{
                    input: {
                      endAdornment: (
                        <InputAdornment position='end'>
                          <IconButton
                            aria-label='toggle confirm new password visibility'
                            onClick={handleClickShowConfirmNewPassword}
                            onMouseDown={handleMouseDownPassword}
                            edge='end'
                          >
                            {showConfrimNewPassword ? <VisibilityOffOutlined /> : <VisibilityOutlined />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    },

                    htmlInput: { placeholder: 'Confirm new password' },
                  }}
                />
              )}
            />
            <div className='mt-[20px] flex justify-between gap-[30px]'>
              <ActionButton color='inherit' variant='outlined' fullWidth type='reset' onClick={onModalClose}>
                Cancel
              </ActionButton>
              <ActionButton color='primary' variant='contained' fullWidth type='submit'>
                Change Password
              </ActionButton>
            </div>
          </form>
        </ModalBody>
      </Modal>
      <ModalLoadingBackdrop open={loading} />
    </>
  );
};

ChangePasswordModal.propTypes = {
  email: PropTypes.string,
  open: PropTypes.bool,
  handleClose: PropTypes.func,
};

export default ChangePasswordModal;
